import { useState, useCallback, useRef, useEffect } from 'react';
import hybridMappingService from '@/lib/hybridMappingService';
import performanceMonitor from '@/lib/performanceMonitor';
import minimalSecurity from '@/lib/security/minimalSecurity';

export const useAddressSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clientIP, setClientIP] = useState('unknown');
  const debounceTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Get client IP on component mount (performance optimized)
  useEffect(() => {
    const getClientIP = async () => {
      const ipServices = [
        'https://api.ipify.org?format=json',
        'https://ipapi.co/json/',
        'https://ipinfo.io/json'
      ];
      
      for (const service of ipServices) {
        try {
          const response = await fetch(service);
          const data = await response.json();
          const ip = data.ip || data.query || 'unknown';
          if (ip !== 'unknown') {
            setClientIP(ip);
            return;
          }
        } catch (error) {
          // IP service failed, continue to next service
        }
      }
      
      // Fallback to localhost for development
      setClientIP('127.0.0.1');
    };
    
    getClientIP();
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!input || input.length < 1) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Minimal security validation (proceed with sanitized input rather than aborting)
    const securityResult = minimalSecurity.fastSecurityCheck(input, 'query', clientIP);
    const safeQuery = securityResult?.sanitized || input;

    // Show loading immediately for better perceived performance
    setIsLoading(true);

    // Debounce the API call with reduced delay
    debounceTimeoutRef.current = setTimeout(async () => {
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      try {
        // Places-first (Google Places Autocomplete → learned_locations)
        const data = await hybridMappingService.getGooglePlacesSuggestions(
          safeQuery,
          'US',
          clientIP
        );
        
        // Check if request was cancelled
        if (!abortControllerRef.current.signal.aborted) {
          setSuggestions(data || []);
        }
      } catch (error) {
        // Only log error if it's not an abort error
        if (error.name !== 'AbortError') {
          console.error('Error fetching address suggestions:', {
            message: error.message,
            query: safeQuery,
            stack: error.stack,
            clientIP: clientIP
          });
          setSuggestions([]);
          
          // Track error with more context
          performanceMonitor.trackError('search_error', 'address_suggestions', {
            query: safeQuery,
            error: error.message,
            clientIP: clientIP,
            timestamp: new Date().toISOString()
          });
        }
      } finally {
        // Only update loading state if request wasn't cancelled
        if (!abortControllerRef.current.signal.aborted) {
          setIsLoading(false);
        }
      }
        }, 120);
  }, [clientIP]);

  /** Persist selected Google place to learned_locations */
  const recordSelection = useCallback(async (suggestion, query = '') => {
    try {
      await hybridMappingService.recordSelectedSuggestion(suggestion, query);
    } catch (error) {
      console.error('Failed to record selected suggestion:', error);
    }
  }, []);

  // Cleanup function to cancel pending requests
  const cancelPendingRequests = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
  }, []);

  return { 
    suggestions, 
    isLoading, 
    fetchSuggestions, 
    setSuggestions, 
    cancelPendingRequests,
    recordSelection,
  };
};
